/**
 * ViewportSelector Component
 * Week 2: Custom Viewport UI with 20+ Device Presets
 *
 * Features:
 * - Dropdown with 24 device presets grouped by category
 * - Current viewport display with dimensions
 * - Custom viewport creation
 * - Custom viewport management (edit/delete)
 */

import React, { useState, useEffect } from "react";
import { useResponsiveStore } from "@/stores/editor/responsive-store";
import { VIEWPORT_PRESETS, presetToBreakpoint } from "@/stores/editor/responsive-store";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronDown, Plus, Monitor, Smartphone, Tablet } from "lucide-react";
import { cn } from "@/lib/utils";
import { CustomViewportModal } from "./CustomViewportModal";
import {
  FOREST_GREEN,
  LIGHT_GRAY,
  MID_GRAY,
} from "@/styles/tokens";

interface ViewportSelectorProps {
  className?: string;
  showDimensions?: boolean;
}

export const ViewportSelector: React.FC<ViewportSelectorProps> = ({
  className,
  showDimensions = true,
}) => {
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [announcement, setAnnouncement] = useState<string>('');

  const {
    activeViewport,
    availableViewports,
    customViewports,
    setActiveViewport,
  } = useResponsiveStore();

  // Week 2 Day 5 Fix: Ensure activeViewport is in availableViewports after rehydration
  // When page refreshes, activeViewport is restored but availableViewports resets to defaults
  // This effect adds the activeViewport preset if it's missing
  useEffect(() => {
    if (!activeViewport) return;

    // Check if activeViewport exists in availableViewports
    const exists = availableViewports.some((vp) => vp.id === activeViewport);

    if (!exists) {
      // Check if it's a known preset
      const preset = VIEWPORT_PRESETS[activeViewport];
      if (preset) {
        // Add the preset as a viewport
        const breakpoint = presetToBreakpoint(preset);
        useResponsiveStore.getState().addViewport(breakpoint);
      }
    }
  }, [activeViewport, availableViewports]);

  // Get current viewport config
  const currentViewport = availableViewports.find(
    (vp) => vp.id === activeViewport
  );

  // Group presets by category
  const presetsByCategory = {
    mobile: Object.values(VIEWPORT_PRESETS).filter(
      (preset) => preset.category === "mobile"
    ),
    tablet: Object.values(VIEWPORT_PRESETS).filter(
      (preset) => preset.category === "tablet"
    ),
    desktop: Object.values(VIEWPORT_PRESETS).filter(
      (preset) => preset.category === "desktop"
    ),
    breakpoint: Object.values(VIEWPORT_PRESETS).filter(
      (preset) => preset.category === "breakpoint"
    ),
  };

  // Handle viewport selection
  const handleSelectViewport = (viewportId: string) => {
    // Check if it's a preset
    const preset = VIEWPORT_PRESETS[viewportId];
    if (preset) {
      // Convert preset to ResponsiveBreakpoint and add if not already available
      const existingViewport = availableViewports.find(
        (vp) => vp.id === viewportId
      );
      if (!existingViewport) {
        // Add the preset as a viewport
        const breakpoint = presetToBreakpoint(preset);
        useResponsiveStore.getState().addViewport(breakpoint);
      }
    }

    // Set as active viewport
    setActiveViewport(viewportId);

    // Week 2 Day 6.3: Announce viewport change for screen readers
    const selectedViewport = availableViewports.find((vp) => vp.id === viewportId) || preset;
    if (selectedViewport) {
      const announcementText = `Viewport changed to ${selectedViewport.name}, ${selectedViewport.width} by ${selectedViewport.height} pixels`;
      setAnnouncement(announcementText);
      // Clear announcement after a delay to allow it to be read
      setTimeout(() => setAnnouncement(''), 3000);
    }
  };

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "mobile":
        return <Smartphone className="w-4 h-4" />;
      case "tablet":
        return <Tablet className="w-4 h-4" />;
      case "desktop":
        return <Monitor className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <TooltipProvider>
      <div className={cn("flex items-center gap-2", className)}>
        {/* Week 2 Day 6.3: Screen reader announcement region */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {announcement}
        </div>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-8 min-w-[160px] justify-between px-2.5",
                    "transition-all duration-200 ease-out",
                    "active:scale-[0.98]"
                  )}
                  style={{
                    // Hover states applied via CSS, but the border color uses token
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = `${FOREST_GREEN.light}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '';
                  }}
                  aria-label={
                    currentViewport
                      ? `Current viewport: ${currentViewport.name}, ${currentViewport.width} by ${currentViewport.height} pixels. Click to change viewport.`
                      : "Select viewport"
                  }
                  aria-haspopup="menu"
                >
                  <div className="flex items-center gap-1.5">
                    {currentViewport?.icon && (
                      <span className="text-sm transition-transform duration-200 group-hover:scale-110" aria-hidden="true">
                        {currentViewport.icon}
                      </span>
                    )}
                    <span className="text-sm font-medium">
                      {currentViewport?.name || "Select Viewport"}
                    </span>
                  </div>
                  {showDimensions && currentViewport && (
                    <span
                      className="text-[11px] ml-1.5"
                      style={{ color: MID_GRAY.DEFAULT }}
                      aria-hidden="true"
                      data-testid="viewport-dimensions"
                    >
                      {currentViewport.width}×{currentViewport.height}
                    </span>
                  )}
                  <ChevronDown className="w-3.5 h-3.5 ml-1.5 transition-transform duration-200" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="center">
              <p className="text-sm">
                {currentViewport
                  ? `${currentViewport.name} (${currentViewport.width}×${currentViewport.height})`
                  : "Choose a viewport to preview your design"}
              </p>
              <p className="text-xs mt-1" style={{ color: MID_GRAY.light }}>
                Click to see all available viewports
              </p>
            </TooltipContent>
          </Tooltip>

        <DropdownMenuContent
          align="start"
          className="w-[280px] max-h-[500px] overflow-y-auto"
          aria-label="Viewport selection menu"
        >
          {/* Current Viewports (Desktop/Tablet/Mobile) */}
          <DropdownMenuGroup>
            <DropdownMenuLabel>Current Viewports</DropdownMenuLabel>
            {availableViewports
              .filter((vp) => !vp.isCustom && ["mobile", "tablet", "desktop"].includes(vp.id))
              .map((viewport) => (
                <DropdownMenuItem
                  key={viewport.id}
                  onClick={() => handleSelectViewport(viewport.id)}
                  className={cn(
                    "flex items-center gap-2",
                    "transition-all duration-200 ease-out",
                    "hover:pl-3 hover:bg-accent/80",
                    activeViewport === viewport.id && "bg-accent font-medium border-l-2 border-primary"
                  )}
                >
                  <span className="text-lg transition-transform duration-200 hover:scale-110">{viewport.icon}</span>
                  <span className="flex-1">{viewport.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {viewport.width}×{viewport.height}
                  </span>
                </DropdownMenuItem>
              ))}
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* Mobile Devices */}
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex items-center gap-2">
              {getCategoryIcon("mobile")}
              Mobile Devices
            </DropdownMenuLabel>
            {presetsByCategory.mobile.map((preset) => (
              <DropdownMenuItem
                key={preset.id}
                onClick={() => handleSelectViewport(preset.id)}
                className={cn(
                  "flex items-center gap-2 pl-6",
                  "transition-all duration-200 ease-out",
                  "hover:pl-7 hover:bg-accent/80",
                  activeViewport === preset.id && "bg-accent font-medium border-l-2 border-primary"
                )}
              >
                <span className="flex-1">{preset.name}</span>
                <span className="text-xs text-muted-foreground">
                  {preset.width}×{preset.height}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* Tablet Devices */}
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex items-center gap-2">
              {getCategoryIcon("tablet")}
              Tablet Devices
            </DropdownMenuLabel>
            {presetsByCategory.tablet.map((preset) => (
              <DropdownMenuItem
                key={preset.id}
                onClick={() => handleSelectViewport(preset.id)}
                className={cn(
                  "flex items-center gap-2 pl-6",
                  "transition-all duration-200 ease-out",
                  "hover:pl-7 hover:bg-accent/80",
                  activeViewport === preset.id && "bg-accent font-medium border-l-2 border-primary"
                )}
              >
                <span className="flex-1">{preset.name}</span>
                <span className="text-xs text-muted-foreground">
                  {preset.width}×{preset.height}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* Desktop Devices */}
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex items-center gap-2">
              {getCategoryIcon("desktop")}
              Desktop Devices
            </DropdownMenuLabel>
            {presetsByCategory.desktop.map((preset) => (
              <DropdownMenuItem
                key={preset.id}
                onClick={() => handleSelectViewport(preset.id)}
                className={cn(
                  "flex items-center gap-2 pl-6",
                  "transition-all duration-200 ease-out",
                  "hover:pl-7 hover:bg-accent/80",
                  activeViewport === preset.id && "bg-accent font-medium border-l-2 border-primary"
                )}
              >
                <span className="flex-1">{preset.name}</span>
                <span className="text-xs text-muted-foreground">
                  {preset.width}×{preset.height}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* Common Breakpoints */}
          <DropdownMenuGroup>
            <DropdownMenuLabel>Common Breakpoints</DropdownMenuLabel>
            {presetsByCategory.breakpoint.map((preset) => (
              <DropdownMenuItem
                key={preset.id}
                onClick={() => handleSelectViewport(preset.id)}
                className={cn(
                  "flex items-center gap-2 pl-6",
                  "transition-all duration-200 ease-out",
                  "hover:pl-7 hover:bg-accent/80",
                  activeViewport === preset.id && "bg-accent font-medium border-l-2 border-primary"
                )}
              >
                <span className="text-lg transition-transform duration-200 hover:scale-110">{preset.icon}</span>
                <span className="flex-1">{preset.name}</span>
                <span className="text-xs text-muted-foreground">
                  {preset.width}×{preset.height}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>

          {/* Custom Viewports */}
          {customViewports.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuLabel>Custom Viewports</DropdownMenuLabel>
                {customViewports.map((viewport) => (
                  <DropdownMenuItem
                    key={viewport.id}
                    onClick={() => handleSelectViewport(viewport.id)}
                    className={cn(
                      "flex items-center gap-2 pl-6",
                      "transition-all duration-200 ease-out",
                      "hover:pl-7 hover:bg-accent/80",
                      activeViewport === viewport.id && "bg-accent font-medium border-l-2 border-primary"
                    )}
                  >
                    <span className="text-lg transition-transform duration-200 hover:scale-110">{viewport.icon}</span>
                    <span className="flex-1">{viewport.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {viewport.width}×{viewport.height}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </>
          )}

          <DropdownMenuSeparator />

          {/* Create Custom Viewport */}
          <DropdownMenuItem
            onClick={() => setIsCustomModalOpen(true)}
            className="flex items-center gap-2 transition-all duration-200 ease-out hover:pl-3 hover:font-medium"
            style={{ color: FOREST_GREEN.DEFAULT }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${FOREST_GREEN.DEFAULT}1A`}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            data-testid="add-custom-viewport"
          >
            <Plus className="w-4 h-4 transition-transform duration-200 hover:rotate-90" />
            <span>Create Custom Viewport</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Custom Viewport Modal */}
      <CustomViewportModal
        isOpen={isCustomModalOpen}
        onClose={() => setIsCustomModalOpen(false)}
      />
    </div>
    </TooltipProvider>
  );
};
